import type { CreditProduct } from '~/composables/credit';

import { pilarCreditWhatsAppNumber } from '~/constants';

const CREDIT_WHATSAPP_MESSAGE =
	'Olá, gostaria de mais informações sobre Financiamento';
export const CREDIT_WHATSAPP_URL = `https://wa.me/${pilarCreditWhatsAppNumber}?text=${encodeURIComponent(CREDIT_WHATSAPP_MESSAGE)}`;

export const CREDIT_PRODUCT_TABS: { product: CreditProduct; label: string }[] =
	[
		{ product: 'financing', label: 'Financiar' },
		{ product: 'consortium', label: 'Consórcio' },
		{ product: 'homeEquity', label: 'CGI' }
	];

export const CREDIT_PRODUCT_NAMES: Record<CreditProduct, string> = {
	financing: 'financiamento',
	consortium: 'consórcio',
	homeEquity: 'Crédito com Garantia de Imóvel'
};

export const CREDIT_INTENT_QUESTIONS: Record<
	CreditProduct,
	{ title: string; options: string[] }
> = {
	financing: {
		title: 'Você já escolheu o imóvel?',
		options: ['Já escolhi', 'Estou visitando', 'Só pesquisando']
	},
	consortium: {
		title: 'Quando pretende usar a carta?',
		options: ['Em até 1 ano', 'De 1 a 3 anos', 'Sem pressa']
	},
	homeEquity: {
		title: 'Para que vai usar o crédito?',
		options: ['Quitar dívidas', 'Negócio ou investimento', 'Reforma', 'Outro']
	}
};

export const CREDIT_INCOME_OPTIONS = [
	'Até R$ 15 mil',
	'R$ 15 a 30 mil',
	'R$ 30 a 60 mil',
	'Acima de R$ 60 mil'
];

export const CREDIT_HERO_BENEFITS = [
	'Comparamos o custo efetivo total (CET), não só a taxa',
	'Atendimento exclusivo, conduzido por especialistas de crédito imobiliário',
	'Acompanhamento personalizado em todas as etapas do processo'
];

export const CREDIT_HERO_STATS = [
	{ value: 'R$ 4,5 bi', label: 'em imóveis negociados nos últimos 12 meses' },
	{ value: '5 bancos', label: 'comparados em uma só análise' }
];

export const CREDIT_PARTNERS = [
	'Bradesco',
	'Itaú',
	'Santander',
	'Caixa',
	'Volpi Tech'
];

export const CREDIT_GOALS: {
	product: CreditProduct;
	category: string;
	badge?: string;
	title: string;
	description: string;
	cta: string;
}[] = [
	{
		product: 'financing',
		category: 'Financiamento',
		badge: 'Mais procurado',
		title: 'Quero comprar um imóvel',
		description:
			'Financie em até 35 anos, com taxas a partir de 11,19% a.a. + TR. Use FGTS na entrada quando o imóvel se enquadra no SFH.',
		cta: 'Simular financiamento'
	},
	{
		product: 'consortium',
		category: 'Consórcio',
		title: 'Quero comprar sem pagar juros',
		description:
			'Parcelas menores, sem juros, para quem pode planejar. A carta é liberada por sorteio ou lance — e o FGTS pode entrar no lance.',
		cta: 'Simular consórcio'
	},
	{
		product: 'homeEquity',
		category: 'Crédito com Garantia de Imóvel – CGI',
		title: 'Quero levantar capital com meu imóvel',
		description:
			'Até 60% do valor do imóvel, em até 20 anos, com juros muito menores que os do crédito pessoal. Você continua morando ou recebendo o aluguel.',
		cta: 'Simular CGI'
	}
];

export const CREDIT_HOW_IT_WORKS = [
	{
		number: '01',
		title: 'Receba propostas',
		description: 'Clique no botão e preencha suas informações.'
	},
	{
		number: '02',
		title: 'Um especialista analisa',
		description:
			'Entendemos seu perfil e enviamos para os bancos com mais chance de aprovar nas melhores condições.'
	},
	{
		number: '03',
		title: 'Compare lado a lado',
		description: 'Propostas reais com taxa, custo total e parcela.'
	},
	{
		number: '04',
		title: 'Do sim à chave',
		description:
			'Cuidamos de avaliação, documentação e registro, em sintonia com o seu corretor.'
	}
];

export const CREDIT_COMPARISON_COLUMNS = [
	'Financiamento',
	'Consórcio',
	'Crédito com Garantia de Imóvel (CGI)'
];

export const CREDIT_COMPARISON_ROWS: { label: string; values: string[] }[] = [
	{
		label: 'Ideal para',
		values: [
			'Comprar agora',
			'Comprar com planejamento, sem juros',
			'Levantar dinheiro com um imóvel que já é seu'
		]
	},
	{
		label: 'Custo de referência',
		values: [
			'A partir de 11,19%',
			'Sem juros; taxa de adm. média de 0,073% a.m.',
			'A partir de 1,65% ao mês'
		]
	},
	{
		label: 'Quando o dinheiro sai',
		values: [
			'Após a conclusão do registro em cartório.',
			'Na contemplação (sorteio ou lance)',
			'Após a conclusão do registro em cartório.'
		]
	},
	{
		label: 'Prazo',
		values: ['Até 35 anos', 'Média de 207 meses', 'Até 20 anos']
	},
	{
		label: 'Quanto cobre',
		values: [
			'De 80% a 90% do imóvel.',
			'100% da carta, sem entrada',
			'Até 60% do imóvel'
		]
	},
	{
		label: 'Usa FGTS?',
		values: [
			'Sim, dentro das regras de enquadramento.',
			'Sim, no lance ou para completar a carta',
			'Não'
		]
	}
];

export const CREDIT_BANK_RATES = [
	{ bank: 'Caixa', rate: '11,19%' },
	{ bank: 'Itaú', rate: '11,49%' },
	{ bank: 'Bradesco', rate: '11,60%' },
	{ bank: 'Santander', rate: '11,69%' }
];

export const CREDIT_REASONS = [
	{
		title: 'Tranquilidade sem custo',
		description:
			'Especialistas cuidam de cada detalhe do processo, sem taxa de assessoria.'
	},
	{
		title: 'Análise multibancos',
		description:
			'Consultamos diferentes bancos em um só lugar, buscando as melhores condições para o seu financiamento, sem precisar ir de agência em agência.'
	},
	{
		title: 'Foco em exclusividade',
		description: 'Avaliação personalizada para perfis de alta renda.'
	}
];

export const CREDIT_FAQ = [
	{
		question: 'A Pilar cobra pela assessoria?',
		answer: 'Não. Nossa assessoria é 100% gratuita.'
	},
	{
		question: 'Quanto preciso ter de entrada?',
		answer:
			'Em geral, entre 10% e 20% do valor do imóvel. E você pode usar o saldo do FGTS para compor esse valor.'
	},
	{
		question: 'Até que valor de imóvel posso usar o FGTS?',
		answer:
			'Com o novo teto do SFH, você pode usar o saldo do seu FGTS em imóveis de até R$ 2,25 milhões, seja para dar entrada, amortizar ou até quitar o financiamento.'
	},
	{
		question: 'SAC ou Price: qual escolher?',
		answer:
			'A melhor escolha depende do seu perfil, da sua renda e dos seus planos. Nós trazemos todas as simulações lado a lado para você comparar e decidir com tranquilidade.'
	},
	{
		question: 'Como funciona a contemplação no consórcio?',
		answer:
			'Todo mês há um sorteio entre os participantes em dia e uma rodada de lances: quem oferece o maior lance é contemplado. Lances competitivos costumam ficar entre 30% e 50% da carta, e o FGTS pode ser usado no lance.'
	},
	{
		question: 'Quanto posso levantar com o CGI?',
		answer:
			'Em geral, é possível obter até 60% do valor de avaliação do imóvel, com prazo de até 240 meses. O imóvel fica como garantia da operação, mas você mantém o direito de morar nele ou receber renda com a locação.'
	},
	{
		question: 'Quanto tempo leva?',
		answer:
			'Em média, 60 dias. Ao comparar as propostas, mostramos o prazo estimado de cada banco.'
	},
	{
		question: 'Meus dados estão seguros?',
		answer:
			'Seus dados são utilizados exclusivamente para a análise e busca de propostas de crédito, sendo compartilhados apenas com as instituições financeiras envolvidas, sempre em conformidade com a LGPD.'
	}
];
